import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Card,
  CardContent,
  Divider,
  Chip,
  CircularProgress
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import ShipmentTrackingMap from './ShipmentTrackingMap';
import ShipmentList from './ShipmentList';
import ShipmentDetails from './ShipmentDetails';
import { shipmentService } from '../../services/shipment.service';

const ShipmentTrackingPage = () => {
  const [selectedShipmentId, setSelectedShipmentId] = useState(null);
  const [shipments, setShipments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    const fetchShipments = async () => {
      try {
        setLoading(true);
        // Using shipmentService instead of direct axios call
        const response = await shipmentService.getShipments();
        
        console.log('Shipments response:', response); // Debug log
        
        // The response is directly an array of shipments
        if (Array.isArray(response)) {
          // Ensure each shipment has a unique ID
          const shipmentsWithUniqueIds = response.map((shipment, index) => ({
            ...shipment,
            // Use a combination of shipment_id and index to ensure uniqueness
            uniqueKey: `${shipment.shipment_id}_${index}`
          }));
          
          setShipments(shipmentsWithUniqueIds);
          
          // Auto-select the latest shipment
          if (shipmentsWithUniqueIds.length > 0) {
            // Find the latest shipment based on estimated_delivery_date or created_at
            const latestShipment = findLatestShipment(shipmentsWithUniqueIds);
            console.log('Auto-selecting latest shipment:', latestShipment.shipment_id);
            setSelectedShipmentId(latestShipment.shipment_id);
          }
        } else {
          throw new Error('Unexpected response format: expected an array');
        }
        
        setLoading(false);
      } catch (err) {
        console.error('Error fetching shipments:', err);
        setError('Failed to load shipments. Please try again later.');
        setLoading(false);
      }
    };
    
    fetchShipments();
  }, []);
  
  // Helper function to find the latest shipment
  const findLatestShipment = (shipments) => {
    if (!shipments || shipments.length === 0) return null;
    
    // Try to sort by estimated_delivery_date if available
    if (shipments[0].estimated_delivery_date) {
      return [...shipments].sort((a, b) => {
        return new Date(b.estimated_delivery_date) - new Date(a.estimated_delivery_date);
      })[0];
    }
    
    // Try to sort by created_at if available
    if (shipments[0].created_at) {
      return [...shipments].sort((a, b) => {
        return new Date(b.created_at) - new Date(a.created_at);
      })[0];
    }
    
    // If no date fields are available, use the first shipment
    // Or alternatively, you could sort by shipment_id if it contains a date component
    if (shipments[0].shipment_id && shipments[0].shipment_id.includes('-')) {
      return [...shipments].sort((a, b) => {
        return b.shipment_id.localeCompare(a.shipment_id);
      })[0];
    }
    
    // Default to the first shipment if no sorting is possible
    return shipments[0];
  };
  
  const handleShipmentSelect = (shipmentId) => {
    setSelectedShipmentId(shipmentId);
  };
  
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1, p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Shipment Tracking
      </Typography>
      <Grid container spacing={3}>
        {/* Shipment List */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, height: '80vh', overflow: 'auto' }}>
            <ShipmentList 
              shipments={shipments} 
              selectedShipmentId={selectedShipmentId}
              onShipmentSelect={handleShipmentSelect}
            />
          </Paper>
        </Grid>
        
        {/* Map and Details */}
        <Grid item xs={12} md={8}>
          <Grid container spacing={3}>
            {/* Map */}
            <Grid item xs={12}>
              <Paper sx={{ p: 2, height: '50vh' }}>
                <ShipmentTrackingMap selectedShipmentId={selectedShipmentId} />
              </Paper>
            </Grid>
            
            {/* Details */}
            <Grid item xs={12}>
              <Paper sx={{ p: 2, height: '25vh', overflow: 'auto' }}>
                <ShipmentDetails 
                  selectedShipmentId={selectedShipmentId} 
                  shipments={shipments}
                />
              </Paper>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ShipmentTrackingPage;